/**
 * Canonical location taxonomy for Grey Dates.
 * Used by browse filters, profile creation/editing, and backend validation.
 * Keep backend/src/data/locationTaxonomy.js in sync.
 */

export const DEFAULT_COUNTRY_CODE = "CH";

export const COUNTRIES = [
  {
    code: "DO",
    name: "Dominican Republic",
    regionLabel: "Provinces",
    regions: [
      "Distrito Nacional",
      "Santo Domingo",
      "Santiago",
      "La Altagracia",
      "San Cristóbal",
      "La Vega",
      "Puerto Plata",
      "San Pedro de Macorís",
      "Duarte",
      "Espaillat",
    ],
    cities: [
      "Santo Domingo",
      "Santiago",
      "Higüey",
      "La Vega",
      "San Cristóbal",
      "San Pedro de Macorís",
      "San Francisco de Macorís",
      "Boca Chica",
      "Moca",
      "Punta Cana",
    ],
  },
  {
    code: "FR",
    name: "France",
    regionLabel: "Regions",
    regions: [
      "Île-de-France",
      "Auvergne-Rhône-Alpes",
      "Nouvelle-Aquitaine",
      "Occitanie",
      "Hauts-de-France",
      "Grand Est",
      "Provence-Alpes-Côte d'Azur",
      "Pays de la Loire",
      "Normandy",
      "Brittany",
    ],
    cities: [
      "Paris",
      "Marseille",
      "Lyon",
      "Toulouse",
      "Nice",
      "Nantes",
      "Montpellier",
      "Strasbourg",
      "Bordeaux",
      "Lille",
    ],
  },
  {
    code: "DE",
    name: "Germany",
    regionLabel: "Bundesländer",
    regions: [
      "North Rhine-Westphalia",
      "Bavaria",
      "Baden-Württemberg",
      "Lower Saxony",
      "Hesse",
      "Saxony",
      "Rhineland-Palatinate",
      "Berlin",
      "Schleswig-Holstein",
      "Brandenburg",
    ],
    cities: [
      "Berlin",
      "Hamburg",
      "Munich",
      "Cologne",
      "Frankfurt am Main",
      "Düsseldorf",
      "Leipzig",
      "Stuttgart",
      "Dortmund",
      "Bremen",
    ],
  },
  {
    code: "IT",
    name: "Italy",
    regionLabel: "Regions",
    regions: [
      "Lombardy",
      "Lazio",
      "Campania",
      "Veneto",
      "Sicily",
      "Emilia-Romagna",
      "Piedmont",
      "Apulia",
      "Tuscany",
      "Calabria",
    ],
    cities: [
      "Rome",
      "Milan",
      "Naples",
      "Turin",
      "Palermo",
      "Genoa",
      "Bologna",
      "Florence",
      "Bari",
      "Catania",
    ],
  },
  {
    code: "ES",
    name: "Spain",
    regionLabel: "Autonomous Communities",
    regions: [
      "Andalusia",
      "Catalonia",
      "Community of Madrid",
      "Valencian Community",
      "Galicia",
      "Castile and León",
      "Basque Country",
      "Canary Islands",
      "Castile-La Mancha",
      "Region of Murcia",
    ],
    cities: [
      "Madrid",
      "Barcelona",
      "Valencia",
      "Zaragoza",
      "Seville",
      "Málaga",
      "Murcia",
      "Palma",
      "Las Palmas de Gran Canaria",
      "Alicante",
    ],
  },
  {
    code: "CH",
    name: "Switzerland",
    regionLabel: "Cantons",
    regions: [
      "Zürich",
      "Bern",
      "Vaud",
      "Aargau",
      "St. Gallen",
      "Geneva",
      "Lucerne",
      "Ticino",
      "Valais",
      "Fribourg",
    ],
    cities: [
      "Zürich",
      "Geneva",
      "Basel",
      "Lausanne",
      "Bern",
      "Winterthur",
      "Lucerne",
      "St. Gallen",
      "Lugano",
      "Biel/Bienne",
    ],
  },
  {
    code: "US",
    name: "United States",
    regionLabel: "States",
    regions: [
      "California",
      "Texas",
      "Florida",
      "New York",
      "Pennsylvania",
      "Illinois",
      "Ohio",
      "Georgia",
      "North Carolina",
      "Michigan",
    ],
    cities: [
      "New York City",
      "Los Angeles",
      "Chicago",
      "Houston",
      "Phoenix",
      "Philadelphia",
      "San Antonio",
      "San Diego",
      "Dallas",
      "Fort Worth",
    ],
  },
];

/** Default map viewport per browse country (capital / main hub). */
const COUNTRY_MAP_CENTERS = {
  "Dominican Republic": { lat: 18.4861, lng: -69.9312 },
  France: { lat: 48.8566, lng: 2.3522 },
  Germany: { lat: 52.52, lng: 13.405 },
  Italy: { lat: 41.9028, lng: 12.4964 },
  Spain: { lat: 40.4168, lng: -3.7038 },
  Switzerland: { lat: 47.3769, lng: 8.5417 },
  "United States": { lat: 40.7128, lng: -74.006 },
};

/** City center coordinates for map pins (city name → lat/lng). */
const CITY_MAP_CENTERS = {
  "Santo Domingo": { lat: 18.4861, lng: -69.9312 },
  Santiago: { lat: 19.4517, lng: -70.6973 },
  "Punta Cana": { lat: 18.5601, lng: -68.3725 },
  Paris: { lat: 48.8566, lng: 2.3522 },
  Nice: { lat: 43.7102, lng: 7.262 },
  Marseille: { lat: 43.2965, lng: 5.3698 },
  Lyon: { lat: 45.764, lng: 4.8357 },
  Berlin: { lat: 52.52, lng: 13.405 },
  Munich: { lat: 48.1351, lng: 11.582 },
  Hamburg: { lat: 53.5511, lng: 9.9937 },
  Rome: { lat: 41.9028, lng: 12.4964 },
  Milan: { lat: 45.4642, lng: 9.19 },
  Florence: { lat: 43.7696, lng: 11.2558 },
  Madrid: { lat: 40.4168, lng: -3.7038 },
  Barcelona: { lat: 41.3874, lng: 2.1686 },
  Zürich: { lat: 47.3769, lng: 8.5417 },
  Geneva: { lat: 46.2044, lng: 6.1432 },
  Basel: { lat: 47.5596, lng: 7.5886 },
  Lausanne: { lat: 46.5197, lng: 6.6323 },
  Bern: { lat: 46.948, lng: 7.4474 },
  "New York City": { lat: 40.7128, lng: -74.006 },
  "Los Angeles": { lat: 34.0522, lng: -118.2437 },
  Chicago: { lat: 41.8781, lng: -87.6298 },
  Miami: { lat: 25.7617, lng: -80.1918 },
};

const CITY_NAME_ALIASES = {
  zurich: "Zürich",
  zürich: "Zürich",
  geneva: "Geneva",
  "genève": "Geneva",
  "geneve": "Geneva",
  basel: "Basel",
  "los angeles": "Los Angeles",
  "new york": "New York City",
  "new york city": "New York City",
  florence: "Florence",
  "firenze": "Florence",
};

const DEFAULT_MAP_CENTER = COUNTRY_MAP_CENTERS.Switzerland;

function resolveCountry(countryRef) {
  if (!countryRef) return null;
  return (
    COUNTRIES.find((c) => c.code === countryRef || c.name === countryRef) ?? null
  );
}

export function getCountriesAlphabetical() {
  return [...COUNTRIES].sort((a, b) => a.name.localeCompare(b.name));
}

export function getCountryByCode(code) {
  return COUNTRIES.find((c) => c.code === code) ?? null;
}

export function getCountryByName(name) {
  return COUNTRIES.find((c) => c.name === name) ?? null;
}

export function getDefaultCountry() {
  return getCountryByCode(DEFAULT_COUNTRY_CODE) ?? COUNTRIES[0];
}

export function getCountryMapCenter(countryRef) {
  const country = resolveCountry(countryRef);
  if (!country) return DEFAULT_MAP_CENTER;
  return COUNTRY_MAP_CENTERS[country.name] ?? DEFAULT_MAP_CENTER;
}

export function normalizeCityName(city) {
  if (!city) return "";
  const trimmed = String(city).trim();
  const alias = CITY_NAME_ALIASES[trimmed.toLowerCase()];
  return alias ?? trimmed;
}

export function getCityMapCenter(city, countryRef = null) {
  const normalized = normalizeCityName(city);
  if (normalized && CITY_MAP_CENTERS[normalized]) {
    return CITY_MAP_CENTERS[normalized];
  }

  if (countryRef) {
    return getCountryMapCenter(countryRef);
  }

  return null;
}

export function isSupportedCountry(countryRef) {
  return Boolean(resolveCountry(countryRef));
}

export function normalizeCountry(countryRef) {
  const country = resolveCountry(countryRef);
  return country?.name ?? getDefaultCountry().name;
}

export function getRegionsForCountry(countryRef) {
  return resolveCountry(countryRef)?.regions ?? [];
}

export function getRegionLabelForCountry(countryRef) {
  return resolveCountry(countryRef)?.regionLabel ?? "Region";
}

export function getRegionConfigForCountry(countryRef) {
  const country = resolveCountry(countryRef);
  if (!country) return null;
  return { label: country.regionLabel, items: country.regions };
}

export function getCitiesForCountry(countryRef) {
  return resolveCountry(countryRef)?.cities ?? [];
}

export function isValidRegion(countryRef, region) {
  if (!region) return false;
  return getRegionsForCountry(countryRef).includes(region);
}

export function isValidCity(countryRef, city) {
  if (!city) return false;
  return getCitiesForCountry(countryRef).includes(city);
}

export function isValidCountryCode(code) {
  return Boolean(getCountryByCode(code));
}
